/**
 * Word-style editor for Notes and task notes: fonts, sizes, colours, highlight, headings, bullets, numbers,
 * check lists, alignment, indent, links, pictures (upload, paste or drop), tables, sub/superscript, and a
 * full-screen mode. Saves HTML; the server keeps only a safe subset.
 */
import { EditorContent, useEditor, useEditorState, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Color, FontFamily, FontSize, TextStyle } from "@tiptap/extension-text-style";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import Image from "@tiptap/extension-image";
import { Table, TableCell, TableHeader, TableRow } from "@tiptap/extension-table";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import Placeholder from "@tiptap/extension-placeholder";
import clsx from "clsx";
import {
  AlignCenter, AlignJustify, AlignLeft, AlignRight, Bold, Eraser, Highlighter, ImagePlus, IndentDecrease, IndentIncrease, Italic, Link2, List,
  ListChecks, ListOrdered, Maximize2, Minimize2, Minus, Palette, Redo2, Strikethrough, Subscript as SubIcon, Superscript as SupIcon, Table as TableIcon, Underline as UIcon, Undo2,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

const FONTS = [["", "Default"], ["Arial", "Arial"], ["Calibri, Carlito, sans-serif", "Calibri"], ["Georgia", "Georgia"], ["'Times New Roman', Times, serif", "Times New Roman"], ["Verdana", "Verdana"], ["'Trebuchet MS'", "Trebuchet MS"], ["'Courier New', monospace", "Courier New"], ["'Plus Jakarta Sans', sans-serif", "Jakarta"]] as const;
const SIZES = ["", "10px", "11px", "12px", "14px", "16px", "18px", "20px", "24px", "28px", "32px", "40px"];
const COLORS = ["#000000", "#434343", "#666666", "#999999", "#c00000", "#e53935", "#f57c00", "#fbc02d", "#2e7d32", "#00897b", "#1565c0", "#2b35ff", "#6a1b9a", "#ad1457", "#795548", "#ffffff"];
const HIGHLIGHTS = ["#fff59d", "#ffe0b2", "#ffcdd2", "#c8e6c9", "#b3e5fc", "#e1bee7", "#f5f5f5"];

export async function uploadFile(f: File): Promise<{ id: string; filename: string; contentType: string }> {
  const fd = new FormData();
  fd.append("file", f);
  const res = await fetch("/api/documents", { method: "POST", body: fd, credentials: "include" });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.message ?? `Couldn't upload ${f.name}`);
  return body;
}

function Btn({ on, title, onClick, children, disabled }: { on?: boolean; title: string; onClick: () => void; children: React.ReactNode; disabled?: boolean }) {
  return (
    <button type="button" title={title} aria-label={title} disabled={disabled} onMouseDown={(e) => e.preventDefault()} onClick={onClick}
      className={clsx("inline-flex h-8 min-w-8 items-center justify-center rounded-md px-1.5 text-[var(--text-2)] hover:bg-black/[0.06] disabled:opacity-30", on && "bg-brand-50 text-brand-700")}>
      {children}
    </button>
  );
}
const Sep = () => <span className="mx-0.5 h-5 w-px bg-[var(--line)]" />;

function Swatches({ colors, onPick, onClear, label }: { colors: readonly string[]; onPick: (c: string) => void; onClear: () => void; label: string }) {
  return (
    <span className="absolute left-0 top-9 z-40 w-48 rounded-xl border border-[var(--line)] bg-[var(--panel)] p-2 shadow-[var(--shadow-pop)]">
      <span className="mb-1 block text-xs font-semibold">{label}</span>
      <span className="grid grid-cols-8 gap-1">
        {colors.map((c) => <button key={c} type="button" aria-label={c} onMouseDown={(e) => e.preventDefault()} onClick={() => onPick(c)} className="h-5 w-5 rounded border border-black/10" style={{ background: c }} />)}
      </span>
      <button type="button" className="muted mt-2 text-xs hover:underline" onMouseDown={(e) => e.preventDefault()} onClick={onClear}>None</button>
    </span>
  );
}

function Toolbar({ editor, onImage, full, setFull, uploading }: { editor: Editor; onImage: () => void; full: boolean; setFull: (v: boolean) => void; uploading: boolean }) {
  const [pop, setPop] = useState<"color" | "highlight" | "table" | null>(null);
  const st = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive("bold"), italic: e.isActive("italic"), underline: e.isActive("underline"), strike: e.isActive("strike"),
      sub: e.isActive("subscript"), sup: e.isActive("superscript"), bullet: e.isActive("bulletList"), ordered: e.isActive("orderedList"),
      task: e.isActive("taskList"), link: e.isActive("link"), table: e.isActive("table"),
      h: e.isActive("heading", { level: 1 }) ? "h1" : e.isActive("heading", { level: 2 }) ? "h2" : e.isActive("heading", { level: 3 }) ? "h3" : e.isActive("blockquote") ? "quote" : e.isActive("codeBlock") ? "code" : "p",
      align: (["center", "right", "justify"] as const).find((a) => e.isActive({ textAlign: a })) ?? "left",
      font: (e.getAttributes("textStyle").fontFamily as string | undefined) ?? "",
      size: (e.getAttributes("textStyle").fontSize as string | undefined) ?? "",
      canUndo: e.can().undo(), canRedo: e.can().redo(),
    }),
  });
  const c = () => editor.chain().focus();
  const setStyle = (v: string) => {
    const ch = c();
    if (v === "p") ch.setParagraph().run();
    else if (v === "quote") ch.toggleBlockquote().run();
    else if (v === "code") ch.toggleCodeBlock().run();
    else ch.toggleHeading({ level: Number(v[1]) as 1 | 2 | 3 }).run();
  };
  const link = () => {
    const prev = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("Link address (https://…)", prev ?? "https://");
    if (url === null) return;
    if (!url || url === "https://") c().extendMarkRange("link").unsetLink().run();
    else c().extendMarkRange("link").setLink({ href: /^(https?:|mailto:|tel:)/.test(url) ? url : `https://${url}` }).run();
  };
  const sel = "h-8 rounded-md border border-[var(--line)] bg-[var(--panel)] px-1.5 text-xs";
  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-[var(--line)] bg-[var(--panel)] px-2 py-1.5" onMouseLeave={() => setPop(null)}>
      <Btn title="Undo (⌘Z)" onClick={() => c().undo().run()} disabled={!st.canUndo}><Undo2 size={15} /></Btn>
      <Btn title="Redo (⌘⇧Z)" onClick={() => c().redo().run()} disabled={!st.canRedo}><Redo2 size={15} /></Btn>
      <Sep />
      <select className={sel} value={st.h} onChange={(e) => setStyle(e.target.value)} aria-label="Style">
        <option value="p">Normal</option><option value="h1">Heading 1</option><option value="h2">Heading 2</option><option value="h3">Heading 3</option><option value="quote">Quote</option><option value="code">Code</option>
      </select>
      <select className={clsx(sel, "w-28")} value={st.font} onChange={(e) => (e.target.value ? c().setFontFamily(e.target.value).run() : c().unsetFontFamily().run())} aria-label="Font">
        {FONTS.map(([v, l]) => <option key={l} value={v} style={{ fontFamily: v || undefined }}>{l}</option>)}
      </select>
      <select className={clsx(sel, "w-16")} value={st.size} onChange={(e) => (e.target.value ? c().setFontSize(e.target.value).run() : c().unsetFontSize().run())} aria-label="Size">
        {SIZES.map((s) => <option key={s} value={s}>{s ? s.replace("px", "") : "Size"}</option>)}
      </select>
      <Sep />
      <Btn title="Bold (⌘B)" on={st.bold} onClick={() => c().toggleBold().run()}><Bold size={15} /></Btn>
      <Btn title="Italic (⌘I)" on={st.italic} onClick={() => c().toggleItalic().run()}><Italic size={15} /></Btn>
      <Btn title="Underline (⌘U)" on={st.underline} onClick={() => c().toggleUnderline().run()}><UIcon size={15} /></Btn>
      <Btn title="Strikethrough" on={st.strike} onClick={() => c().toggleStrike().run()}><Strikethrough size={15} /></Btn>
      <Btn title="Subscript" on={st.sub} onClick={() => c().toggleSubscript().run()}><SubIcon size={15} /></Btn>
      <Btn title="Superscript" on={st.sup} onClick={() => c().toggleSuperscript().run()}><SupIcon size={15} /></Btn>
      <span className="relative">
        <Btn title="Text colour" onClick={() => setPop(pop === "color" ? null : "color")}><Palette size={15} /></Btn>
        {pop === "color" && <Swatches label="Text colour" colors={COLORS} onPick={(col) => { c().setColor(col).run(); setPop(null); }} onClear={() => { c().unsetColor().run(); setPop(null); }} />}
      </span>
      <span className="relative">
        <Btn title="Highlight" onClick={() => setPop(pop === "highlight" ? null : "highlight")}><Highlighter size={15} /></Btn>
        {pop === "highlight" && <Swatches label="Highlight" colors={HIGHLIGHTS} onPick={(col) => { c().setHighlight({ color: col }).run(); setPop(null); }} onClear={() => { c().unsetHighlight().run(); setPop(null); }} />}
      </span>
      <Sep />
      <Btn title="Align left" on={st.align === "left"} onClick={() => c().setTextAlign("left").run()}><AlignLeft size={15} /></Btn>
      <Btn title="Centre" on={st.align === "center"} onClick={() => c().setTextAlign("center").run()}><AlignCenter size={15} /></Btn>
      <Btn title="Align right" on={st.align === "right"} onClick={() => c().setTextAlign("right").run()}><AlignRight size={15} /></Btn>
      <Btn title="Justify" on={st.align === "justify"} onClick={() => c().setTextAlign("justify").run()}><AlignJustify size={15} /></Btn>
      <Sep />
      <Btn title="Bullets" on={st.bullet} onClick={() => c().toggleBulletList().run()}><List size={15} /></Btn>
      <Btn title="Numbering" on={st.ordered} onClick={() => c().toggleOrderedList().run()}><ListOrdered size={15} /></Btn>
      <Btn title="Check list" on={st.task} onClick={() => c().toggleTaskList().run()}><ListChecks size={15} /></Btn>
      <Btn title="Decrease indent" onClick={() => (editor.can().liftListItem("listItem") ? c().liftListItem("listItem").run() : c().liftListItem("taskItem").run())}><IndentDecrease size={15} /></Btn>
      <Btn title="Increase indent" onClick={() => (editor.can().sinkListItem("listItem") ? c().sinkListItem("listItem").run() : c().sinkListItem("taskItem").run())}><IndentIncrease size={15} /></Btn>
      <Sep />
      <Btn title="Link" on={st.link} onClick={link}><Link2 size={15} /></Btn>
      <Btn title={uploading ? "Uploading…" : "Picture"} onClick={onImage} disabled={uploading}><ImagePlus size={15} /></Btn>
      <span className="relative">
        <Btn title="Table" on={st.table} onClick={() => (st.table ? setPop(pop === "table" ? null : "table") : c().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run())}><TableIcon size={15} /></Btn>
        {pop === "table" && st.table && (
          <span className="absolute left-0 top-9 z-40 grid w-48 gap-0.5 rounded-xl border border-[var(--line)] bg-[var(--panel)] p-1.5 text-left text-xs shadow-[var(--shadow-pop)]">
            {([["Row above", () => c().addRowBefore().run()], ["Row below", () => c().addRowAfter().run()], ["Column left", () => c().addColumnBefore().run()], ["Column right", () => c().addColumnAfter().run()],
              ["Delete row", () => c().deleteRow().run()], ["Delete column", () => c().deleteColumn().run()], ["Merge / split cells", () => c().mergeOrSplit().run()], ["Header row on/off", () => c().toggleHeaderRow().run()], ["Delete table", () => c().deleteTable().run()]] as const)
              .map(([l, fn]) => <button key={l} type="button" className="rounded px-2 py-1 text-left hover:bg-black/[0.05]" onMouseDown={(e) => e.preventDefault()} onClick={() => { fn(); setPop(null); }}>{l}</button>)}
          </span>
        )}
      </span>
      <Btn title="Line" onClick={() => c().setHorizontalRule().run()}><Minus size={15} /></Btn>
      <Btn title="Clear formatting" onClick={() => c().unsetAllMarks().clearNodes().run()}><Eraser size={15} /></Btn>
      <span className="flex-1" />
      <Btn title={full ? "Exit full screen (Esc)" : "Full screen"} onClick={() => setFull(!full)}>{full ? <Minimize2 size={15} /> : <Maximize2 size={15} />}</Btn>
    </div>
  );
}

export function RichEditor({ value, onChange, placeholder = "Write here…", minHeight = 260, title, onUploaded, editable = true }:
  { value: string; onChange: (html: string) => void; placeholder?: string; minHeight?: number; title?: string; onUploaded?: (doc: { id: string; filename: string }) => void; editable?: boolean }) {
  const [full, setFull] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const file = useRef<HTMLInputElement>(null);
  const editorRef = useRef<Editor | null>(null);

  const insertImages = async (files: File[]) => {
    const imgs = files.filter((f) => f.type.startsWith("image/"));
    if (!imgs.length) return false;
    setUploading(true);
    setErr(null);
    try {
      for (const f of imgs) {
        const d = await uploadFile(f);
        editorRef.current?.chain().focus().setImage({ src: `/api/documents/${d.id}/content`, alt: d.filename }).run();
        onUploaded?.(d);
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setUploading(false);
    }
    return true;
  };

  const editor = useEditor({
    editable,
    extensions: [
      StarterKit.configure({ link: { openOnClick: false, autolink: true } }),
      TextStyle, Color, FontFamily, FontSize, Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Image.configure({ inline: false }),
      Table.configure({ resizable: true }), TableRow, TableHeader, TableCell,
      TaskList, TaskItem.configure({ nested: true }), Subscript, Superscript,
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate: ({ editor: e }) => onChange(e.isEmpty ? "" : e.getHTML()),
    editorProps: {
      attributes: { class: "rich-doc outline-none" },
      handlePaste: (_v, ev) => {
        // Word, Pages and PDFs put a picture of the first page on the clipboard next to the real text: paste the
        // text (all of it); only a clipboard that is just a picture (a screenshot, a copied image) becomes one
        const cb = ev.clipboardData;
        const files = Array.from(cb?.files ?? []);
        const text = cb?.getData("text/plain") ?? "";
        const html = cb?.getData("text/html") ?? "";
        const htmlText = html.replace(/<img[^>]*>/gi, "").replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").trim();
        if (text.trim() || htmlText) return false;
        if (files.some((f) => f.type.startsWith("image/"))) { void insertImages(files); return true; }
        return false;
      },
      handleDrop: (_v, ev) => {
        const files = Array.from((ev as DragEvent).dataTransfer?.files ?? []);
        if (files.some((f) => f.type.startsWith("image/"))) { ev.preventDefault(); void insertImages(files); return true; }
        return false;
      },
    },
  });
  editorRef.current = editor;

  useEffect(() => {
    if (!full) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setFull(false);
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [full]);

  if (!editor) return null;
  return (
    <div className={clsx("overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--panel)]", full && "fixed inset-0 z-50 flex flex-col rounded-none border-0")}>
      {full && title && <div className="border-b border-[var(--line)] px-5 py-3 text-sm font-semibold">{title}</div>}
      {editable && <div className={clsx(full ? "" : "sticky top-0 z-10")}><Toolbar editor={editor} onImage={() => file.current?.click()} full={full} setFull={setFull} uploading={uploading} /></div>}
      {err && <p className="px-4 pt-2 text-xs text-red-700">{err}</p>}
      <div className={clsx("cursor-text", full ? "flex-1 overflow-auto bg-[#f4f5fb] py-8" : "")} onClick={() => editor.chain().focus().run()}>
        <div className={clsx(full ? "mx-auto min-h-full max-w-[52rem] rounded-lg bg-white px-14 py-12 shadow-sm" : "px-5 py-4")} style={full ? undefined : { minHeight }}>
          <EditorContent editor={editor} />
        </div>
      </div>
      <input ref={file} type="file" accept="image/*" multiple hidden onChange={(e) => { void insertImages(Array.from(e.target.files ?? [])); e.target.value = ""; }} />
    </div>
  );
}
